import React, { useState, useEffect } from 'react';
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import CircularProgress from "@mui/joy/CircularProgress";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Select from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import Input from "@mui/joy/Input";
import Button from "@mui/joy/Button";
import { LineChart } from "@mui/x-charts/LineChart";
import { PaymentSummaryApi } from '../paymentMethods/paymentSummaryApi';
import { StatsRangeRequest, TimeGranularity, DualDataResponse, DataPoint } from '../../../../models/paymentSummary';
import { formatRubles, kopecksToRublesNumber } from '../../../../common/money';

export function ChartsTab() {
    const [loading, setLoading] = useState(false);
    const [profitData, setProfitData] = useState<DualDataResponse | null>(null);
    const [fromDate, setFromDate] = useState<string>('');
    const [toDate, setToDate] = useState<string>('');
    const [granularity, setGranularity] = useState<TimeGranularity>(TimeGranularity.Day);
    const [amountsLoading, setAmountsLoading] = useState(false);
    const [amountsData, setAmountsData] = useState<DualDataResponse | null>(null);
    const [countsLoading, setCountsLoading] = useState(false);
    const [countsData, setCountsData] = useState<DualDataResponse | null>(null);

    const loadProfitData = async () => {
        setLoading(true);
        try {
            const request: StatsRangeRequest = {
                fromUtc: fromDate || undefined,
                toUtc: toDate || undefined,
                granularity
            };
            const response = await PaymentSummaryApi.getProfit(request);
            setProfitData(response);
        } catch (error) {
            console.error('Error loading profit data:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadAmountsData = async () => {
        setAmountsLoading(true);
        try {
            const request: StatsRangeRequest = {
                fromUtc: fromDate || undefined,
                toUtc: toDate || undefined,
                granularity
            };
            const response = await PaymentSummaryApi.getAmounts(request);
            setAmountsData(response);
        } catch (error) {
            console.error('Error loading amounts data:', error);
        } finally {
            setAmountsLoading(false);
        }
    };

    const loadCountsData = async () => {
        setCountsLoading(true);
        try {
            const request: StatsRangeRequest = {
                fromUtc: fromDate || undefined,
                toUtc: toDate || undefined,
                granularity
            };
            const response = await PaymentSummaryApi.getCounts(request);
            setCountsData(response);
        } catch (error) {
            console.error('Error loading counts data:', error);
        } finally {
            setCountsLoading(false);
        }
    };

    useEffect(() => {
        loadProfitData();
        loadAmountsData();
        loadCountsData();
    }, []);

    

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString('ru-RU', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const prepareProfitDataset = (deposits: DataPoint[], withdrawals: DataPoint[]) => {
        const allDates = new Set([
            ...deposits.map(d => d.bucketUtc),
            ...withdrawals.map(w => w.bucketUtc)
        ]);

        return Array.from(allDates)
            .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
            .map(date => {
                const dep = deposits.find(d => d.bucketUtc === date);
                const wd = withdrawals.find(w => w.bucketUtc === date);
                return {
                    x: new Date(date),
                    depositProfit: dep ? kopecksToRublesNumber(dep.value) : null,
                    withdrawalProfit: wd ? kopecksToRublesNumber(wd.value) : null,
                };
            });
    };

    const sumPoints = (points: DataPoint[]): number => points.reduce((acc, p) => acc + (p?.value ?? 0), 0);

    const renderProfitChart = () => {
        if (!profitData || (profitData.deposits.length === 0 && profitData.withdrawals.length === 0)) {
            return (
                <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                    Нет данных для отображения
                </Box>
            );
        }

        const profitDataset = prepareProfitDataset(profitData.deposits, profitData.withdrawals);
        
        return (
            <Box sx={{ mb: 3 }}>
                <Typography level="title-md" sx={{ mb: 2 }}>
                    График профита
                </Typography>
                <Box
                    sx={{
                        p: 2,
                        bgcolor: 'background.level1',
                        borderRadius: '8px',
                        border: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    <LineChart
                        dataset={profitDataset}
                        xAxis={[{ dataKey: 'x', scaleType: 'time', valueFormatter: (v: Date) => v.toLocaleString('ru-RU') }]}
                        series={[
                            { dataKey: 'depositProfit', label: 'Профит с пополнений (руб.)', color: '#2e7d32', connectNulls: true, curve: 'monotoneX', showMark: true, valueFormatter: (v: number | null) => v != null ? `${v.toLocaleString('ru-RU')} ₽` : '—' },
                            { dataKey: 'withdrawalProfit', label: 'Профит с выводов (руб.)', color: '#d32f2f', connectNulls: true, curve: 'monotoneX', showMark: true, valueFormatter: (v: number | null) => v != null ? `${v.toLocaleString('ru-RU')} ₽` : '—' },
                        ]}
                        height={400}
                    />
                </Box>
            </Box>
        );
    };

    const prepareAmountsDataset = (deposits: DataPoint[], withdrawals: DataPoint[]) => {
        const allDates = new Set([
            ...deposits.map(d => d.bucketUtc),
            ...withdrawals.map(w => w.bucketUtc)
        ]);

        return Array.from(allDates)
            .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
            .map(date => {
                const dep = deposits.find(d => d.bucketUtc === date);
                const wd = withdrawals.find(w => w.bucketUtc === date);
                return {
                    x: new Date(date),
                    depositRub: dep ? kopecksToRublesNumber(dep.value) : null,
                    withdrawalRub: wd ? kopecksToRublesNumber(wd.value) : null,
                };
            });
    };

    const renderAmountsChart = () => {
        if (!amountsData || (amountsData.deposits.length === 0 && amountsData.withdrawals.length === 0)) {
            return (
                <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                    Нет данных для отображения
                </Box>
            );
        }

        const amountsDataset = prepareAmountsDataset(amountsData.deposits, amountsData.withdrawals);

        return (
            <Box sx={{ mb: 3 }}>
                <Typography level="title-md" sx={{ mb: 2 }}>
                    График пополнений и выводов (руб.)
                </Typography>
                <Box
                    sx={{
                        p: 2,
                        bgcolor: 'background.level1',
                        borderRadius: '8px',
                        border: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    <LineChart
                        dataset={amountsDataset}
                        xAxis={[{ dataKey: 'x', scaleType: 'time', valueFormatter: (v: Date) => v.toLocaleString('ru-RU') }]}
                        series={[
                            { dataKey: 'depositRub', label: 'Пополнения (руб.)', color: '#2e7d32', connectNulls: true, curve: 'monotoneX', showMark: true, valueFormatter: (v: number | null) => v != null ? `${v.toLocaleString('ru-RU')} ₽` : '—' },
                            { dataKey: 'withdrawalRub', label: 'Выводы (руб.)', color: '#d32f2f', connectNulls: true, curve: 'monotoneX', showMark: true, valueFormatter: (v: number | null) => v != null ? `${v.toLocaleString('ru-RU')} ₽` : '—' },
                        ]}
                        height={400}
                    />
                </Box>
            </Box>
        );
    };

    const prepareCountsDataset = (deposits: DataPoint[], withdrawals: DataPoint[]) => {
        const allDates = new Set([
            ...deposits.map(d => d.bucketUtc),
            ...withdrawals.map(w => w.bucketUtc)
        ]);

        return Array.from(allDates)
            .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
            .map(date => {
                const dep = deposits.find(d => d.bucketUtc === date);
                const wd = withdrawals.find(w => w.bucketUtc === date);
                return {
                    x: new Date(date),
                    depositCount: dep ? dep.value : null,
                    withdrawalCount: wd ? wd.value : null,
                };
            });
    };

    const renderCountsChart = () => {
        if (!countsData || (countsData.deposits.length === 0 && countsData.withdrawals.length === 0)) {
            return (
                <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                    Нет данных для отображения
                </Box>
            );
        }

        const countsDataset = prepareCountsDataset(countsData.deposits, countsData.withdrawals);

        return (
            <Box sx={{ mb: 3 }}>
                <Typography level="title-md" sx={{ mb: 2 }}>
                    Количество пополнений и выводов
                </Typography>
                <Box
                    sx={{
                        p: 2,
                        bgcolor: 'background.level1',
                        borderRadius: '8px',
                        border: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    <LineChart
                        dataset={countsDataset}
                        xAxis={[{ dataKey: 'x', scaleType: 'time', valueFormatter: (v: Date) => v.toLocaleString('ru-RU') }]}
                        series={[
                            { dataKey: 'depositCount', label: 'Пополнения (шт.)', color: '#2e7d32', connectNulls: true, curve: 'monotoneX', showMark: true },
                            { dataKey: 'withdrawalCount', label: 'Выводы (шт.)', color: '#d32f2f', connectNulls: true, curve: 'monotoneX', showMark: true },
                        ]}
                        height={400}
                    />
                </Box>
            </Box>
        );
    };

    return (
        <Box sx={{ p: 2 }}>
            <Typography level="h4" sx={{ mb: 3 }}>
                Графики пополнений и выводов
            </Typography>

            {/* Фильтры */}
            <Box
                sx={{
                    display: 'flex',
                    gap: 2,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    p: 2,
                    bgcolor: 'background.level1',
                    borderRadius: '8px',
                    mb: 3
                }}
            >
                <FormControl size="sm" sx={{ minWidth: 150 }}>
                    <FormLabel>От даты</FormLabel>
                    <Input
                        type="datetime-local"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                    />
                </FormControl>

                <FormControl size="sm" sx={{ minWidth: 150 }}>
                    <FormLabel>До даты</FormLabel>
                    <Input
                        type="datetime-local"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                    />
                </FormControl>

                <FormControl size="sm" sx={{ minWidth: 120 }}>
                    <FormLabel>Группировка</FormLabel>
                    <Select
                        value={granularity}
                        onChange={(event, newValue) => setGranularity(newValue as TimeGranularity)}
                    >
                        <Option value={TimeGranularity.Hour}>По часам</Option>
                        <Option value={TimeGranularity.Day}>По дням</Option>
                        <Option value={TimeGranularity.Month}>По месяцам</Option>
                        <Option value={TimeGranularity.Year}>По годам</Option>
                        <Option value={TimeGranularity.All}>За все время</Option>
                    </Select>
                </FormControl>

                <Button onClick={() => { loadProfitData(); loadAmountsData(); loadCountsData(); }} loading={loading || amountsLoading || countsLoading} sx={{ alignSelf: 'end' }}>
                    Обновить
                </Button>
            </Box>

            {/* Итоговые суммы */}
            {profitData && (
                <Box
                    sx={{
                        mb: 3,
                        p: 2,
                        borderRadius: '8px',
                        bgcolor: 'background.level1',
                        border: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    <Typography level="title-md" sx={{ mb: 1 }}>
                        Итого
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', mb: 1 }}>
                        <Box>
                            <Typography level="body-sm" color="neutral">
                                Профит с депозитов
                            </Typography>
                            <Typography level="title-sm" color="success">
                                {formatRubles(profitData.totalDeposits)}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography level="body-sm" color="neutral">
                                Профит с выводов
                            </Typography>
                            <Typography level="title-sm" color="danger">
                                {formatRubles(profitData.totalWithdrawals)}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography level="body-sm" color="neutral">
                                Общий профит
                            </Typography>
                            <Typography 
                                level="title-sm" 
                                color={
                                    (sumPoints(profitData.deposits) + sumPoints(profitData.withdrawals)) >= 0 ? 'success' : 'danger'
                                }
                            >
                                {formatRubles((sumPoints(profitData.deposits) + sumPoints(profitData.withdrawals)))}
                            </Typography>
                        </Box>
                    </Box>
                    {amountsData && (
                        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            <Box>
                                <Typography level="body-sm" color="neutral">
                                    Общие пополнения (руб)
                                </Typography>
                                <Typography level="title-sm" color="success">
                                    {formatRubles(amountsData.totalDeposits)}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography level="body-sm" color="neutral">
                                    Общие выводы (руб)
                                </Typography>
                                <Typography level="title-sm" color="danger">
                                    {formatRubles(amountsData.totalWithdrawals)}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    {countsData && (
                        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', mt: 1 }}>
                            <Box>
                                <Typography level="body-sm" color="neutral">
                                    Количество пополнений
                                </Typography>
                                <Typography level="title-sm" color="success">
                                    {countsData.totalDeposits.toLocaleString('ru-RU')}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography level="body-sm" color="neutral">
                                    Количество выводов
                                </Typography>
                                <Typography level="title-sm" color="danger">
                                    {countsData.totalWithdrawals.toLocaleString('ru-RU')}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Box>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress size="lg" />
                </Box>
            ) : (
                <>
                    {renderProfitChart()}
                    {amountsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress size="lg" />
                        </Box>
                    ) : (
                        renderAmountsChart()
                    )}
                    {countsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress size="lg" />
                        </Box>
                    ) : (
                        renderCountsChart()
                    )}
                </>
            )}
        </Box>
    );
}

